import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MotifIcon } from '../icon/icon';
import { MotifProgressBar } from '../progress-bar/progress-bar';
import { imageIcNavigateBefore24px, imageIcNavigateNext24px } from '../../assets/icons';

/**
 * @typedef Props
 * @prop  {React.ReactNode} children
 * @prop  {'medium' | 'large' | null} [size]
 * @prop  {string} [className]
 * @prop {boolean} [disabled]
 * @prop {'primary' | 'primary-alt' | 'secondary' | 'warn' | 'text' | 'text-alt'} [variant]
 * @prop {() => void} [onClick]
 * @prop {'submit' | 'reset' | 'button' | 'loader'} [type]
 * @prop {'prev' | 'next'} [stepper]
 * @prop {number} [progress]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifButton = (props) => {
  const {
    variant = 'primary',
    className = '',
    disabled = false,
    size = null,
    children,
    stepper = null,
    type = 'submit',
    progress = null,
    onClick = null,
    ...other
  } = props;
  const buttonType = variant === 'text' || variant === 'text-alt' ? 'isText' : 'isButton';
  const isIndeterminate = type === 'loader' && !progress;

  const classes = classNames({
    'motif-text-button-disabled': buttonType === 'isText' && disabled,
    [`motif-button-stepper-${stepper}`]: buttonType === 'isButton' && children && stepper,
    [`motif-${variant}-button`]: buttonType === 'isText',
    [`motif-button motif-button-${variant}`]: buttonType === 'isButton',
    'motif-loader-button': type === 'loader',
    [`motif-button-${size}`]: size,
    [className]: true
  });

  return (
    <button {...other} type={type} className={classes} disabled={disabled} onClick={onClick}>
      {stepper === 'prev' && <MotifIcon src={imageIcNavigateBefore24px} />}
      {children}
      {type === 'loader' && <MotifProgressBar hideLabel value={progress} isIndeterminate={isIndeterminate} />}
      {stepper === 'next' && <MotifIcon src={imageIcNavigateNext24px} />}
    </button>
  );
};

MotifButton.propTypes = {
  variant: PropTypes.oneOf(['primary', 'primary-alt', 'secondary', 'warn', 'text', 'text-alt']),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['medium', 'large', null]),
  children: PropTypes.node,
  type: PropTypes.oneOf(['submit', 'reset', 'button', 'loader']),
  stepper: PropTypes.oneOf(['prev', 'next']),
  progress: PropTypes.number,
  onClick: PropTypes.func
};

export { MotifButton };
